/**
 * YearSelect — the season/year picker shared by the surfaces that slice a
 * contestant's results by calendar year (dashboard Event History, Season
 * Winnings).
 *
 * `undefined` is a real value here: with `allowAll` it means "every year", and
 * the caller gets `undefined` back rather than a sentinel string to unpick.
 * Without `allowAll` the list is exactly `years` — a season board offers the
 * years the contestant actually has data for, not a rolling ten.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** Radix has no empty-string value, so "all years" needs a sentinel. */
const ALL = 'all';

/** The last ten calendar years, newest first — the default offering. */
export function recentYears(count = 10, from = new Date().getFullYear()): number[] {
  return Array.from({ length: count }, (_, i) => from - i);
}

interface YearSelectProps {
  value: number | undefined;
  onChange: (year: number | undefined) => void;
  /** Years to offer, newest first. Defaults to the last ten. */
  years?: number[];
  /** Offer an "All years" choice, which reports `undefined`. */
  allowAll?: boolean;
  allLabel?: string;
  /** Applied to the trigger — callers size and theme it for their surface. */
  className?: string;
  'aria-label'?: string;
}

export function YearSelect({
  value,
  onChange,
  years,
  allowAll = false,
  allLabel = 'All years',
  className,
  'aria-label': ariaLabel,
}: YearSelectProps) {
  const options = years ?? recentYears();

  return (
    <Select
      value={value ? String(value) : allowAll ? ALL : undefined}
      onValueChange={(v) => onChange(v === ALL ? undefined : Number(v))}
    >
      <SelectTrigger className={cn('w-[120px]', className)} aria-label={ariaLabel}>
        <SelectValue placeholder={allowAll ? allLabel : 'Year'} />
      </SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value={ALL}>{allLabel}</SelectItem>}
        {options.map((y) => (
          <SelectItem key={y} value={String(y)}>
            {y}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
